import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { logError } from "../lib/logger";

const AuthCtx = createContext(null);

/**
 * Captura a localização EXATA do dispositivo (GPS do navegador, com permissão)
 * logo após o login e envia ao backend para registrar no audit log.
 * Best-effort: se o usuário negar a permissão ou o GPS falhar, não faz nada
 * (o audit mantém a localização aproximada por IP).
 */
function captureLoginLocation() {
  try {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api
          .post("/auth/login-location", {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          })
          .catch(() => {});
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  } catch {
    // ignore
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=logged
  const [error, setError] = useState("");

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback (hash has session_id),
    // skip the /me check — AuthCallback will exchange the session_id first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(false);
      return;
    }
    // NOTE: sem guard `mounted` — em React.StrictMode (dev) o effect roda 2x e
    // um guard por-closure fazia o resultado da 1ª chamada ser descartado,
    // deixando `user` preso em null (tela "Carregando…" eterna no preview).
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  // Sessão expirou e o refresh falhou (evento disparado pelo interceptor do axios).
  useEffect(() => {
    const onExpired = () => setUser(false);
    window.addEventListener("cm:auth-expired", onExpired);
    return () => window.removeEventListener("cm:auth-expired", onExpired);
  }, []);

  async function login(email, password) {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) localStorage.setItem("cm_token", data.access_token);
      if (data?.refresh_token) localStorage.setItem("cm_refresh", data.refresh_token);
      // Conta logins bem-sucedidos para a heurística de prompt de instalação PWA.
      try {
        const current = Number(localStorage.getItem("cm_login_count") || 0);
        localStorage.setItem("cm_login_count", String(current + 1));
      } catch {
        // ignore storage errors
      }
      setUser(data.user);
      captureLoginLocation();
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function loginWithGoogleSession(sessionId) {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setError("");
    try {
      const { data } = await api.post("/auth/google/exchange", { session_id: sessionId });
      if (data?.access_token) localStorage.setItem("cm_token", data.access_token);
      if (data?.refresh_token) localStorage.setItem("cm_refresh", data.refresh_token);
      try {
        const current = Number(localStorage.getItem("cm_login_count") || 0);
        localStorage.setItem("cm_login_count", String(current + 1));
      } catch {
        // ignore
      }
      setUser(data.user);
      captureLoginLocation();
      return data.user;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function demoLogin() {
    setError("");
    const { data } = await api.post("/auth/public/demo-login");
    if (data?.access_token) localStorage.setItem("cm_token", data.access_token);
    setUser(data.user);
    return data.user;
  }

  async function logout() {
    try {
      await api.post("/auth/logout", { refresh_token: localStorage.getItem("cm_refresh") || undefined });
    } catch (e) {
      logError("Logout request failed (clearing session anyway):", e);
    }
    localStorage.removeItem("cm_token");
    localStorage.removeItem("cm_refresh");
    setUser(false);
  }

  const value = useMemo(
    () => ({ user, setUser, login, loginWithGoogleSession, demoLogin, logout, error, setError }),
    [user, error],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
